import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Alert,
  IconButton,
  InputAdornment,
  Container,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

const SITE_PASSWORD = 'Swagindian1!';
const STORAGE_KEY = 'sikumaran_site_unlocked';

export default function SitePasswordGate({ children }) {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [shake, setShake] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(STORAGE_KEY) === 'true') setIsUnlocked(true);
    } catch (error) {
      console.warn('Site passcode preference could not be read:', error);
    }
  }, []);

  const handleUnlock = (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (passwordInput === SITE_PASSWORD) {
      try {
        localStorage.setItem(STORAGE_KEY, 'true');
      } catch (error) {
        console.warn('Site access will only be remembered for this visit:', error);
      }
      setIsUnlocked(true);
    } else {
      setErrorMsg('Incorrect passcode. Please try again.');
      setShake(true);
      setTimeout(() => setShake(false), 600);
    }
  };

  if (isUnlocked) {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'radial-gradient(ellipse at top, #1a2332 0%, #0d1117 100%)',
        px: 2,
        py: 4,
      }}
    >
      <Container maxWidth="xs">
        <Card
          sx={{
            borderRadius: 4,
            boxShadow: '0 20px 40px rgba(0,0,0,0.5)',
            background: 'rgba(255, 255, 255, 0.96)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            overflow: 'visible',
            animation: shake ? 'shake 0.5s ease-in-out' : 'none',
            '@keyframes shake': {
              '0%, 100%': { transform: 'translateX(0)' },
              '20%, 60%': { transform: 'translateX(-8px)' },
              '40%, 80%': { transform: 'translateX(8px)' },
            },
          }}
        >
          <CardContent sx={{ p: { xs: 3, sm: 4 }, textAlign: 'center' }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, var(--rosewood, #6b141d) 0%, var(--crimson, #d62839) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '-48px auto 16px',
                color: '#fff',
                boxShadow: '0 8px 24px rgba(214, 40, 57, 0.4)',
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 32 }} />
            </Box>

            <Typography
              variant="overline"
              sx={{
                letterSpacing: 2,
                fontWeight: 700,
                color: 'var(--crimson, #d62839)',
                display: 'block',
              }}
            >
              Private Site Access
            </Typography>

            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ color: '#1a2332', mb: 1, fontFamily: "'IBM Plex Mono', monospace" }}
            >
              Sid Kumaran
            </Typography>

            <Typography variant="body2" sx={{ color: '#64748b', mb: 3 }}>
              Enter the site passcode to continue. Administrative actions require separate GitHub sign-in.
            </Typography>

            {errorMsg && (
              <Alert severity="error" sx={{ mb: 2, textAlign: 'left', borderRadius: 2 }}>
                {errorMsg}
              </Alert>
            )}

            <form onSubmit={handleUnlock}>
              <TextField
                fullWidth
                autoFocus
                size="medium"
                type={showPassword ? 'text' : 'password'}
                label="Enter Passcode"
                placeholder="Passcode"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                sx={{
                  mb: 2.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2.5,
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                startIcon={<VpnKeyIcon />}
                sx={{
                  py: 1.4,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  fontSize: '1rem',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, var(--rosewood, #6b141d) 0%, var(--crimson, #d62839) 100%)',
                  boxShadow: '0 4px 15px rgba(214, 40, 57, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, var(--madder, #a11e2b) 0%, var(--cardinal, #bc2332) 100%)',
                  },
                }}
              >
                Unlock Platform
              </Button>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
}
